import { CustomCell, CustomRenderer, GridCellKind } from "@glideapps/glide-data-grid";
import { IComboBoxOptionGrid, IComboBoxProject, ItipoConceptoGrid } from "../../Components/CargaPorTabla/InterfaceTabla";
import CreatableSelect from "react-select/creatable";
import { components } from "react-select";
import { useRef, useState } from "react";

export interface DropdownCellProps {
  readonly kind: "dropdown-cell";
  readonly value: IComboBoxOptionGrid;
  readonly options: IComboBoxOptionGrid[] | ItipoConceptoGrid[] | IComboBoxProject[];
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly width?: string;
  readonly id: string;
  readonly entity?: any;
  readonly className?: string;
  readonly displayData: any;
  readonly alignContent?: "left" | "center";
  readonly isEditableInputText?:boolean;
  readonly onChange:(option: any, id: string, newValue?: string | undefined) => void | Promise<void>;
}

export type DropdownCell = CustomCell<DropdownCellProps>;

const CustomMenu = (p) => {
  const { Menu } = components;
  const { children, ...rest } = p;
  return <Menu {...rest}>{children}</Menu>;
};

const Wrap = (props) => {
  return <div>{props.children}</div>;
};

const PortalWrap = (props) => {
  return <div>{props.children}</div>;
};

export const DropdownRenderer: CustomRenderer<DropdownCell> = {
  kind: GridCellKind.Custom,

  isMatch: (c): c is DropdownCell =>
    c.kind === GridCellKind.Custom && (c.data as any).kind === "dropdown-cell",

  draw: (args, cell) => {
    const { ctx, theme, rect } = args;
    const { value, displayData, placeholder, alignContent } = cell.data;

    ctx.save();
    ctx.beginPath();
    ctx.rect(rect.x, rect.y, rect.width, rect.height);
    ctx.clip();

    const dataFinal = value?.label ?? "";
    const isPlaceholder = !dataFinal;
    const text = isPlaceholder ? (placeholder ?? "") : dataFinal;
    const color = isPlaceholder ? "#c6c6c6" : theme.textDark;

    ctx.fillStyle = color;
    ctx.textBaseline = "middle";

    let x;
    const y = rect.y + rect.height / 2;

    if (alignContent === "center") {
      ctx.textAlign = "center";
      x = rect.x + rect.width / 2;
    } else {
      ctx.textAlign = "left";
      x = rect.x + theme.cellHorizontalPadding;
    }

    ctx.fillText(text, x, y);
    ctx.restore();

    return true;
  },

  provideEditor: () => {
    return p => {
      const { data } = p.value;
      const { id, value, placeholder, disabled, options, isEditableInputText } = data;
      const selectRef = useRef<any>(null);
      const [textInput, setTextInput] = useState(value?.label || "");

      const handlerOnChange = (newValue) => {
        if (newValue) {
          data.onChange(newValue, data.id, newValue?.label);
          p.onFinishedEditing();
        }
      };

      return (
        <Wrap>
          <CreatableSelect
            className="basic-single"
            ref={selectRef}
            classNamePrefix={placeholder}
            {...(isEditableInputText
              ? { inputValue: textInput }
              : { defaultValue: value })}
            isDisabled={disabled}
            isClearable={true}
            isSearchable={true}
            options={options}
            components={{
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null,
              Menu: (props) => (
                <PortalWrap>
                  <CustomMenu className={"click-outside-ignore"} {...props} />
                </PortalWrap>
              ),
            }}
            onCreateOption={(inputValue) => {
              const newOption = {
                value: inputValue,
                label: inputValue,
              } as IComboBoxOptionGrid;
              data.onChange(newOption, id, newOption.label);
              p.onFinishedEditing();
            }}
            onChange={(newValue: any) => {
              handlerOnChange(newValue);
            }}
            onInputChange={(newValue, action) => {
              if (action.action === "input-change") {
                setTextInput(newValue);
              }
            }}
            menuIsOpen={true}
            onBlur={() => {
              const inputValue = selectRef.current?.props?.inputValue;
              const optionsList = selectRef.current?.props?.options || [];

              const optionIsNewElement = optionsList.filter(
                (opt) => opt.value.toLowerCase() === inputValue.toLowerCase()
              );

              if (optionIsNewElement[0]?.__isNew__) {
                if (inputValue) {
                  const newOption = {
                    value: inputValue,
                    label: inputValue,
                  } as IComboBoxOptionGrid;
                  data.onChange(newOption, id, newOption.label);
                }
              }
            }}
            formatCreateLabel={(inputValue) => `Crear: "${inputValue}"`}
            filterOption={(option, rawInput) => {
              const match = option.label
                .toLowerCase()
                .includes(rawInput.toLowerCase());
              return match;
            }}
            isValidNewOption={(inputValue, selectValue, selectOptions) => {
              if (!inputValue) return false;

              const exists = selectOptions.some((opt) =>
                (opt?.label ?? "")
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              );

              return !exists;
            }}
            menuPortalTarget={document.getElementById("portal")}
            menuPlacement="auto"
            autoFocus={true}
            openMenuOnFocus={true}
            styles={{
              control: (provided) => ({
                ...provided,
                border: "none",
                boxShadow: "none",
                backgroundColor: "transparent",
              }),
              menu: (provided) => ({
                ...provided,
                border: "none",
                boxShadow: "none",
              }),
              option: (provided, state) => ({
                ...provided,
                fontSize: "13px",
              }),
              singleValue: (provided) => ({
                ...provided,
                fontSize: "13px",
              }),
            }}
          />
        </Wrap>
      );
    };
  },
};